import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ProductsPageBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const products = [
    {
      id: 1,
      name: "Sour Oozies",
      subtitle: "Tangy & Explosive",
      description: "Bold sour gummy candy that packs a flavor punch in every bite!",
      image: "https://customer-assets.emergentagent.com/job_af784ae6-49de-4cf3-bbe7-0b767949d154/artifacts/dcakou8k_IMG_0202.png",
      bgColor: "from-cyan-400 to-sky-600",
      bgSolid: "#26A5D9",
      textColor: "text-white",
      sectionId: "sour-oozies"
    },
    {
      id: 2,
      name: "Chocolate Dipped",
      subtitle: "Rich & Indulgent",
      description: "Premium Swedish gummies wrapped in smooth milk chocolate. A decadent treat!",
      image: "https://customer-assets.emergentagent.com/job_af784ae6-49de-4cf3-bbe7-0b767949d154/artifacts/7fb9xef7_IMG_0208.png",
      bgColor: "from-indigo-950 to-purple-900",
      bgSolid: "#221D58",
      textColor: "text-white",
      accentColor: "#F5ED3A",
      sectionId: "chocolate-dipped"
    },
    {
      id: 3,
      name: "Crystal Candy",
      subtitle: "Bite Into Magic",
      description: "Sparkle crunch crystals with a soft inside. Experience the magic of 4 fruity flavours!",
      image: "https://customer-assets.emergentagent.com/job_af784ae6-49de-4cf3-bbe7-0b767949d154/artifacts/kjp1q9gi_IMG_0206.png",
      bgColor: "from-indigo-950 to-blue-900",
      bgSolid: "#221D58",
      textColor: "text-white",
      accentColor: "#60A5FA",
      sectionId: "crystal-candy"
    }
  ];

  // Auto-rotate every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [products.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % products.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
  };

  const currentProduct = products[currentSlide];

  return (
    <section 
      className={`relative h-96 overflow-hidden transition-all duration-1000`}
      style={{ backgroundColor: currentProduct.bgSolid || '#26A5D9' }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23FFFFFF%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%227%22 cy=%227%22 r=%223%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full relative">
        {/* Standard Layout */}
        <div className="flex items-center justify-between h-full relative z-10">
          {/* Left Content */}
          <div 
            key={`content-${currentSlide}`}
            className={`flex-1 ${currentProduct.textColor} animate-slideInLeft`}
          >
            <div className="max-w-2xl">
              <div className="mb-4">
                <span className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-lg font-bold mb-4 animate-fadeInUp">
                  🍭 FEATURED COLLECTION
                </span>
              </div>
              
              <h2 
                className="text-4xl md:text-5xl font-black mb-3 leading-none animate-fadeInUp"
                style={currentProduct.accentColor ? { color: currentProduct.accentColor } : {}}
              >
                {currentProduct.name}
              </h2>
              
              <p className="text-xl md:text-2xl font-bold mb-4 opacity-90 animate-fadeInUp">
                {currentProduct.subtitle}
              </p>
              
              <p className="text-lg mb-6 opacity-80 leading-relaxed animate-fadeInUp">
                {currentProduct.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 animate-fadeInUp">
                <a href="/contact#quote" className="bg-white text-gray-800 px-5 py-3 rounded-full text-sm font-black hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center justify-center text-center w-40">
                  REQUEST SAMPLES
                </a>
                <a 
                  href={`#${currentProduct.sectionId}`} 
                  className="border-2 border-white text-white px-5 py-3 rounded-full text-sm font-black hover:bg-white hover:text-gray-800 transition-all duration-300 hover:scale-105 flex items-center justify-center text-center w-40"
                >
                  VIEW PRODUCTS
                </a>
              </div>
            </div>
          </div>

          {/* Right Content - Product Image */}
          <div 
            key={`image-${currentSlide}`}
            className="flex-1 flex justify-center items-center animate-slideInRight"
          >
            <div className="relative group">
              <img
                src={currentProduct.image}
                alt={currentProduct.name}
                className="h-80 w-auto object-contain hover:scale-110 transition-all duration-500 drop-shadow-2xl animate-fadeInUp"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110 z-20"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110 z-20"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentSlide === index 
                ? 'w-8 bg-white' 
                : 'w-2 bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default ProductsPageBanner;
